import { Plural, Trans } from '@lingui/react/macro';
import Button from '@web/components/Shared/Button';
import Dialog from '@web/components/Shared/Dialog';
import Form from 'carbon-react/lib/components/form';
import Typography from 'carbon-react/lib/components/typography';
import { useCallback } from 'react';
import styled from 'styled-components';
import { TimesheetBulkDeleteItem } from '../TimesheetModalContext';
import { TimesheetsList } from './TimesheetsList';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]};
`;

interface DeleteItemsModalProps {
  description: React.ReactNode;
  title: React.ReactNode;
  timesheets: TimesheetBulkDeleteItem[] | undefined;
  onClose: () => void;
  onOk: () => void | Promise<void>;
  open: boolean;
}

function DeleteItemsModal({ description, onClose, onOk, title, timesheets, open }: DeleteItemsModalProps) {
  const handleConfirm = useCallback(async () => {
    await onOk();
    onClose();
  }, [onOk, onClose]);

  return (
    <Dialog
      disableAutoFocus
      disableEscKey={false}
      enableBackgroundUI={false}
      onCancel={onClose}
      open={open}
      showCloseIcon
      title={
        <Typography variant="h1" pb={2}>
          {title}
        </Typography>
      }
      data-cy="delete-timesheet-modal"
      complexForm
    >
      <Form
        stickyFooter
        saveButton={
          <Button variant="destructive" onClick={handleConfirm} ml={1}>
            <Trans>Delete</Trans>
          </Button>
        }
        leftSideButtons={
          <Button variantType="tertiary" onClick={onClose} data-cy="create-timesheet-cancel-button">
            <Trans>Cancel</Trans>
          </Button>
        }
        fieldSpacing={0}
      >
        <Content>
          <Typography display="block">{description}</Typography>
          <TimesheetsList timesheets={timesheets ?? []} />
        </Content>
      </Form>
    </Dialog>
  );
}

type DeleteTimesheetsModalProps = Omit<DeleteItemsModalProps, 'description' | 'title'>;

export function DeleteTimesheetsModal(props: DeleteTimesheetsModalProps) {
  const count = props.timesheets?.length ?? 0;

  return (
    <DeleteItemsModal
      {...props}
      title={<Plural value={count} one="Delete timesheet" other="Delete # timesheets" />}
      description={
        <Plural
          value={count}
          one="The following timesheet will be permanently deleted."
          other="The following timesheets will be permanently deleted."
          _0="You selected zero timesheets."
        />
      }
    />
  );
}

export function DeleteCrewTimesheetsModal(props: DeleteTimesheetsModalProps) {
  const count = props.timesheets?.length ?? 0;

  return (
    <DeleteItemsModal
      {...props}
      title={<Plural value={count} one="Delete crew timesheet" other="Delete # crew timesheets" />}
      description={
        <Plural
          value={count}
          one="The following crew timesheet will be permanently deleted."
          other="The following crew timesheets will be permanently deleted."
          _0="You selected zero crew timesheets."
        />
      }
    />
  );
}

interface DeleteCrewMembersModalProps extends DeleteTimesheetsModalProps {
  crewMemberName?: string;
}

export function DeleteCrewMembersModal({ crewMemberName, ...props }: DeleteCrewMembersModalProps) {
  const count = props.timesheets?.length ?? 0;
  const title =
    count === 1 && crewMemberName ? (
      <Trans>Delete crew member {crewMemberName}</Trans>
    ) : (
      <Plural value={count} one="Delete crew member" other="Delete # crew members" />
    );

  return (
    <DeleteItemsModal
      {...props}
      title={title}
      description={
        <Plural
          value={count}
          one="The following crew member will be permanently deleted."
          other="The following crew members will be permanently deleted."
          _0="You selected zero crew members."
        />
      }
    />
  );
}
